import { SUPPORTED_CHAINS, ENVIO_GRAPHQL_ENDPOINT, ENVIO_API_KEY } from '../../config';
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { Web3ProviderContext } from './context';
import { useAccount, type UseAccountReturnType, useWalletClient, usePublicClient } from 'wagmi';
import { disconnect } from '@wagmi/core';
import { useQueryClient } from '@tanstack/react-query';
import { SwitchChain } from '../../components/SwitchChain';
import { EnvioGraphQLClient, type EnvioGraphQLClientConfig } from '@peeramid-labs/sdk';
import { wagmiConfig } from '../../config';

export const Web3Provider = ({ children }: { children: React.ReactNode }) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { logout: privyLogout } = usePrivy();
  const wagmiAccount: UseAccountReturnType = useAccount();
  const { wallets } = useWallets();
  const [oldAddress, setOldAddress] = useState<string | undefined>(undefined);
  const activeWallet = useCallback(
    () => wallets.find((wallet) => wagmiAccount.address === wallet.address),
    [wallets, wagmiAccount.address]
  );

  // 🚀 PERFORMANCE: Memoize EnvioGraphQLClient to prevent recreation on every render
  const envioClient = useMemo(() => {
    console.log('Initializing EnvioGraphQLClient with endpoint:', ENVIO_GRAPHQL_ENDPOINT, ENVIO_API_KEY);
    return new EnvioGraphQLClient({
      endpoint: ENVIO_GRAPHQL_ENDPOINT,
      apiKey: ENVIO_API_KEY,
      chainId: Number(activeWallet()?.chainId.split(':')[1]),
    } as EnvioGraphQLClientConfig);
  }, [activeWallet]);

  const queryClient = useQueryClient();
  const isLoggingOutRef = useRef(false);

  // Create logout function that doesn't depend on contextValue
  const handleLogout = useCallback(async () => {
    // Guard against re-entrant calls (e.g. a stale 403 triggering processApiError → onAuthFailure → logout)
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    try {
      // Cancel in-flight queries but do NOT clear the cache yet.
      // Clearing here would empty the cache before React re-renders;
      // hooks like useUserProfile would find no cached data and start
      // a new fetch while account is still set → 403 → "Auth failed" toast.
      queryClient.cancelQueries();

      await disconnect(wagmiConfig);
      // After disconnect, wagmiAccount.address becomes undefined →
      // isWalletReady is false → account in context is undefined →
      // UserMenu (and other auth-gated components) unmount.

      await privyLogout();

      // Now safe to clear the cache — query observers have already unmounted
      queryClient.clear();

      localStorage.removeItem('wagmi.store');
      localStorage.removeItem('privy:connections');

      // Also try to clear any other wagmi-related storage
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('wagmi') || key.includes('privy')) {
          localStorage.removeItem(key);
        }
      });
      // Reset state
      setOldAddress(undefined);
      // Add a small delay to ensure everything is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [privyLogout, queryClient]);

  useEffect(() => {
    const currentAddress = wagmiAccount.address;
    const isExternalWallet = activeWallet()?.walletClientType !== 'privy';
    //eslint-disable-next-line
    console.log('wagmiAccount.address', wagmiAccount.address);
    if (oldAddress === undefined) {
      // Initialize oldAddress with the current address on first render or after logout
      setOldAddress(currentAddress);
    } else if (currentAddress !== oldAddress) {
      // If the address has changed
      if (isExternalWallet) {
        console.log('Detected external wallet account change, logging out.');
        handleLogout();
      } else {
        setOldAddress(currentAddress);
      }
    }
  }, [wagmiAccount.address, oldAddress, wallets, activeWallet, handleLogout]);

  // Determine the expected primary wallet: prefer external wallet, fall back to embedded.
  // Prevents race conditions on reconnect where wagmi may briefly resolve the
  // embedded wallet before the external wallet connector restores.
  const expectedWallet = useMemo(() => {
    const externalWallet = wallets.find((w) => w.walletClientType !== 'privy' && w.chainId?.startsWith('eip155'));
    return externalWallet || wallets.find((w) => w.chainId?.startsWith('eip155'));
  }, [wallets]);

  // During logout, wagmi may briefly resolve the embedded wallet after the external
  // connector is removed. The ref check prevents account from flipping to the
  // embedded address (which would fire new queries that return 403).
  const isWalletReady =
    !isLoggingOutRef.current &&
    !!wagmiAccount.address &&
    !!expectedWallet &&
    wagmiAccount.address === expectedWallet.address;

  // Stable getter that reads the ref — no state, no extra re-renders
  const isLoggingOut = useCallback(() => isLoggingOutRef.current, []);

  // 🚀 PERFORMANCE: Memoize context value to prevent unnecessary re-renders of children
  const contextValue = useMemo(
    () => ({
      isExternalWallet: activeWallet()?.walletClientType !== 'privy',
      currentChain: SUPPORTED_CHAINS.find((chain) => chain.id === Number(activeWallet()?.chainId.split(':')[1])),
      account: isWalletReady ? wagmiAccount.address : undefined,
      isConnected: isWalletReady,
      logout: handleLogout,
      isLoggingOut,
      publicClient,
      walletClient,
      envioClient,
    }),
    [
      activeWallet,
      handleLogout,
      isLoggingOut,
      publicClient,
      walletClient,
      envioClient,
      wagmiAccount.address,
      isWalletReady,
    ]
  );

  return (
    <Web3ProviderContext.Provider value={contextValue}>
      {wallets.length > 0 &&
      !!activeWallet() &&
      SUPPORTED_CHAINS.find((chain) => chain.id === Number(activeWallet()?.chainId.split(':')[1])) === undefined ? (
        <SwitchChain />
      ) : (
        children
      )}
    </Web3ProviderContext.Provider>
  );
};

export default Web3Provider;
